import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';

interface Campaign {
  id: string;
  name: string;
  status: string;
  objective: string;
  daily_budget: number | null;
  lifetime_budget: number | null;
  budget_remaining: number | null;
  spend: number;
  impressions: number;
  clicks: number;
  cpc: number;
  cpm: number;
  ctr: number;
  reach: number;
  purchases: number;
  add_to_carts: number;
  start_time: string;
  created_time: string;
}

interface InsightsDetail {
  campaign_name: string;
  spend: number;
  impressions: number;
  clicks: number;
  cpc: number;
  cpm: number;
  ctr: number;
  reach: number;
  frequency: number;
  purchases: number;
  add_to_carts: number;
  leads: number;
}

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_3d', label: 'Last 3 Days' },
  { value: 'last_7d', label: 'Last 7 Days' },
  { value: 'last_14d', label: 'Last 14 Days' },
  { value: 'last_30d', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

const MetaAdsReport: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('last_7d');
  const [configName, setConfigName] = useState('');
  const [noConfig, setNoConfig] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [insights, setInsights] = useState<InsightsDetail | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false, message: '', type: 'success'
  });

  const statusColors: Record<string, string> = {
    ACTIVE: 'adm-chip-green',
    PAUSED: 'adm-chip-amber',
    DELETED: 'adm-chip-red',
    ARCHIVED: 'adm-chip-gray',
  };

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      setNoConfig(false);
      const response = await adminAPI.getMetaAdsCampaigns(datePreset);
      const data = response.data || response;
      setCampaigns(data.campaigns || []);
      setConfigName(data.config_name || '');
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNoConfig(true);
        setCampaigns([]);
      } else {
        const msg = error.response?.data?.error || 'Failed to load campaigns';
        setErrorMsg(msg);
        setCampaigns([]);
      }
    } finally {
      setLoading(false);
    }
  }, [datePreset]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleCampaignClick = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setLoadingInsights(true);
    try {
      const response = await adminAPI.getMetaAdsCampaignInsights(campaign.id, datePreset);
      setInsights(response.data || response);
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to load insights';
      setToast({ show: true, message: msg, type: 'error' });
      setInsights(null);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Summary metrics
  const totalSpend = campaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
  const totalImpressions = campaigns.reduce((sum, c) => sum + (c.impressions || 0), 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + (c.clicks || 0), 0);
  const avgCTR = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
  const totalPurchases = campaigns.reduce((sum, c) => sum + (c.purchases || 0), 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'ACTIVE').length;

  if (noConfig) {
    return (
      <div className="admin-page adm-page">
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📡</div>
          <h2 style={{ color: '#1f2937', marginBottom: '8px' }}>Meta Ads Not Connected</h2>
          <p style={{ color: '#6b7280', maxWidth: '400px', margin: '0 auto 24px' }}>
            Configure your Meta Marketing API credentials to view campaign performance data.
          </p>
          <a href="/admin/meta-ads/config" style={{ display: 'inline-block', padding: '10px 24px', background: '#2563eb', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '600', fontSize: '14px' }}>Configure Meta Ads →</a>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Meta Ads Report{configName && <span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>— {configName}</span>}</h1>
        <div className="adm-topbar-right">
          <select value={datePreset} onChange={e => setDatePreset(e.target.value)} className="adm-page-size-select" style={{ height: 32 }}>
            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button className="adm-btn adm-btn-refresh" onClick={loadCampaigns} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>

      {errorMsg && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#991b1b', fontSize: '14px' }}>⚠️ {errorMsg}</div>}

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Spend', value: `৳${totalSpend.toFixed(2)}`, color: '#7c3aed' },
          { label: 'Impressions', value: totalImpressions.toLocaleString(), color: '#0369a1' },
          { label: 'Clicks', value: totalClicks.toLocaleString(), color: '#0d9488' },
          { label: 'Avg CTR', value: `${avgCTR.toFixed(2)}%`, color: '#ea580c' },
          { label: 'Purchases', value: String(totalPurchases), color: '#059669' },
          { label: 'Active', value: String(activeCampaigns), color: '#2563eb' },
        ].map(item => (
          <div key={item.label} style={{ background: 'rgba(255,255,255,0.72)', borderRadius: '10px', padding: '16px 20px', boxShadow: '0 4px 16px rgba(26,86,219,0.08)', flex: 1, minWidth: '130px', border: '1.5px solid rgba(26,86,219,0.08)' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: item.color, marginTop: '4px', fontFamily: 'Manrope, sans-serif' }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Campaigns Table */}
      <div className="adm-table-card">
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th">Campaign Name</th>
                <th className="adm-th">Status</th>
                <th className="adm-th">Objective</th>
                <th className="adm-th">Daily Budget</th>
                <th className="adm-th">Spend</th>
                <th className="adm-th">Impressions</th>
                <th className="adm-th">Clicks</th>
                <th className="adm-th">CTR</th>
                <th className="adm-th">CPC</th>
                <th className="adm-th">Purchases</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading campaigns…</td></tr>}
              {!loading && campaigns.length === 0 && <tr><td colSpan={10} className="adm-empty">No campaigns found for this period.</td></tr>}
              {!loading && campaigns.map((c: Campaign) => (
                <tr className="adm-row" key={c.id} style={{ cursor: 'pointer' }} onClick={() => handleCampaignClick(c)}>
                  <td className="adm-td"><span className="adm-name-main">{c.name}</span></td>
                  <td className="adm-td"><span className={`adm-chip ${statusColors[c.status] || 'adm-chip-gray'}`}>{c.status}</span></td>
                  <td className="adm-td"><span className="adm-text-muted" style={{ fontSize: 12 }}>{c.objective}</span></td>
                  <td className="adm-td">{c.daily_budget != null ? `৳${c.daily_budget.toFixed(2)}` : '—'}</td>
                  <td className="adm-td"><span style={{ fontWeight: 700, color: '#7c3aed' }}>৳{Number(c.spend||0).toFixed(2)}</span></td>
                  <td className="adm-td">{Number(c.impressions||0).toLocaleString()}</td>
                  <td className="adm-td">{Number(c.clicks||0).toLocaleString()}</td>
                  <td className="adm-td"><span style={{ fontWeight: 700 }}>{Number(c.ctr||0).toFixed(2)}%</span></td>
                  <td className="adm-td">৳{Number(c.cpc||0).toFixed(2)}</td>
                  <td className="adm-td"><span style={{ fontWeight: 700, color: '#059669' }}>{c.purchases}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign Insights Detail Panel */}
      {selectedCampaign && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '600px', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>{selectedCampaign.name}</h3>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>{selectedCampaign.objective} • {selectedCampaign.status}</span>
              </div>
              <button onClick={() => { setSelectedCampaign(null); setInsights(null); }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            {loadingInsights ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                Loading insights...
              </div>
            ) : insights ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                {[
                  { label: 'Spend', value: `৳${insights.spend.toFixed(2)}`, color: '#7c3aed' },
                  { label: 'Impressions', value: insights.impressions.toLocaleString(), color: '#0369a1' },
                  { label: 'Clicks', value: insights.clicks.toLocaleString(), color: '#0d9488' },
                  { label: 'CTR', value: `${insights.ctr.toFixed(2)}%`, color: '#ea580c' },
                  { label: 'CPC', value: `৳${insights.cpc.toFixed(2)}`, color: '#d97706' },
                  { label: 'CPM', value: `৳${insights.cpm.toFixed(2)}`, color: '#be185d' },
                  { label: 'Reach', value: insights.reach.toLocaleString(), color: '#4f46e5' },
                  { label: 'Frequency', value: insights.frequency.toFixed(2), color: '#0891b2' },
                  { label: 'Purchases', value: String(insights.purchases), color: '#059669' },
                  { label: 'Add to Carts', value: String(insights.add_to_carts), color: '#7c3aed' },
                  { label: 'Leads', value: String(insights.leads), color: '#dc2626' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>{item.label}</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: item.color, marginTop: '4px' }}>{item.value}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                No insight data available for this period.
              </div>
            )}
          </div>
        </div>
      )}

      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default MetaAdsReport;
